import React, { createContext, useContext } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import { cn } from 'shared/lib/cn';

type Ctx = { id: string; disabled: boolean };
const SectionCtx = createContext<Ctx | null>(null);
const useSection = () => {
  const c = useContext(SectionCtx);
  if (!c) throw new Error('Section.Bar/Body must be inside Section.Root');
  return c;
};

export type RootProps = {
  id: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
};

function Root({ id, open, defaultOpen = true, onOpenChange, disabled = false, className, children }: RootProps) {
  return (
    <SectionCtx.Provider value={{ id, disabled }}>
      <Collapsible.Root
        id={`section-${id}`}
        data-section={id}
        data-disabled={disabled ? 'true' : undefined}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        className={cn('scroll-mt-20 rounded-[var(--radius-2)] border border-hairline/15 bg-surface', className)}
      >
        {children}
      </Collapsible.Root>
    </SectionCtx.Provider>
  );
}

export type BarProps = {
  title: string;
  role?: string;
  /** The collapsed stat line. A string, not a node: the Bar draws the span (with
   *  the `<id>-summary` test id) and gives it the text as a hover title, since it
   *  truncates on a narrow bar (CONF-218). */
  summary?: string;
  trailing?: React.ReactNode;   // e.g. the tier on/off switch — must not be inside the trigger
};

function Bar({ title, role, summary, trailing }: BarProps) {
  const { id, disabled } = useSection();
  return (
    <div className="flex items-center gap-3 px-4 py-2">
      <Collapsible.Trigger
        aria-controls={`section-${id}-body`}
        className="group flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
      >
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted transition-transform group-data-[state=closed]:-rotate-90" />
        <span className={cn('font-display text-sm font-semibold tracking-wide', disabled ? 'text-ink-muted' : 'text-ink')}>{title}</span>
        {role && <span className="hidden text-xs text-ink-muted md:inline">{role}</span>}
        {summary && <span data-testid={`${id}-summary`} title={summary} className="ml-auto truncate font-code text-xs text-ink-muted">{summary}</span>}
      </Collapsible.Trigger>
      {trailing}
    </div>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  const { id, disabled } = useSection();
  return (
    <Collapsible.Content
      id={`section-${id}-body`}
      aria-disabled={disabled ? 'true' : undefined}
      className={cn('border-t border-hairline/10 px-4 py-4', disabled && 'pointer-events-none opacity-40')}
    >
      {children}
    </Collapsible.Content>
  );
}

export const Section = { Root, Bar, Body };
